import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../services/supabase';
import './AuthCallback.css';

const PasswordReset: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handlePasswordReset = async () => {
      try {
        // Check if we have the necessary tokens in the URL hash fragment
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (accessToken && refreshToken) {
          // Set the session with the tokens
          const { error } = await supabaseClient.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (error) {
            console.error('Password reset error:', error);
            setError(error.message);
          } else {
            setSuccess(true);
          }
        } else {
          setError('Invalid password reset link');
        }
      } catch (err) {
        console.error('Password reset error:', err);
        setError('An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    handlePasswordReset();
  }, []);

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    try {
      const { error } = await supabaseClient.auth.updateUser({
        password: newPassword
      });

      if (error) {
        setPasswordError(error.message);
      } else {
        setPasswordUpdated(true);
        // Redirect to home after successful password update
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err) {
      setPasswordError('An unexpected error occurred');
    }
  };

  if (loading) {
    return (
      <div className="auth-callback">
        <div className="auth-callback-content">
          <div className="auth-callback-spinner"></div>
          <h2>Verifying reset link...</h2>
          <p>Please wait while we verify your password reset link.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-callback">
        <div className="auth-callback-content">
          <div className="auth-callback-error">
            <h2>Password Reset Error</h2>
            <p>{error}</p>
            <button 
              className="auth-callback-button"
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success && !passwordUpdated) {
    return (
      <div className="auth-callback">
        <div className="auth-callback-content">
          <div className="auth-callback-success">
            <h2>Reset Link Verified</h2>
            <p>Please enter your new password below.</p>
            <form onSubmit={handlePasswordUpdate} className="password-reset-form">
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              {passwordError && (
                <div className="password-error">
                  {passwordError}
                </div>
              )}
              <button type="submit" className="auth-callback-button">
                Update Password
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (success && passwordUpdated) {
    return (
      <div className="auth-callback">
        <div className="auth-callback-content">
          <div className="auth-callback-success">
            <h2>Password Updated Successfully!</h2>
            <p>Your password has been updated. Redirecting you to the home page...</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PasswordReset;
